import { useState, useEffect } from 'react';
import { IconPlus, IconDeviceFloppy } from '@tabler/icons-react';
import { fetchPromoCodes, addPromoCode, updatePromoCode } from '../lib/storeData';
import { showToast } from '../components/ui/Toast';

function todayInGuyana() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guyana' });
}

function niceDate(isoDate) {
  return new Date(isoDate + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AdminPromos() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [newCode, setNewCode] = useState('');
  const [newDiscount, setNewDiscount] = useState('10');
  const [newExpiry, setNewExpiry] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchPromoCodes().then(res => {
      if (cancelled) return;
      setPromos(res.promos);
      setLoadError(res.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  async function reload() {
    const res = await fetchPromoCodes();
    setPromos(res.promos);
    setLoadError(res.error);
  }

  async function addPromo() {
    if (busy) return;
    setBusy(true);
    const res = await addPromoCode(newCode, newDiscount, newExpiry || null);
    setBusy(false);
    if (res.error) {
      showToast(res.error, 'error');
      return;
    }
    showToast('Promo code added');
    setNewCode('');
    setNewDiscount('10');
    setNewExpiry('');
    reload();
  }

  async function toggle(p) {
    if (busy) return;
    setBusy(true);
    const res = await updatePromoCode(p.id, { is_active: !p.is_active });
    setBusy(false);
    if (res.error) {
      showToast(res.error, 'error');
      return;
    }
    showToast(p.code + (p.is_active ? ' turned off' : ' turned on'));
    reload();
  }

  if (loading) {
    return <p className="adm-sub">Loading promo codes...</p>;
  }

  const today = todayInGuyana();

  return (
    <div>
      <h1 className="adm-title">Promo codes</h1>
      <p className="adm-sub">Create discount codes for abandoned cart emails or promotions. Codes can't be renamed or deleted, so turn one off when you no longer want it used.</p>

      <div className="adm-card">
        <div className="adm-card-title">Your codes</div>
        {loadError && (
          <p style={{ fontSize: 12, color: '#8B2020' }}>Could not load promo codes. Check your internet connection and refresh the page.</p>
        )}
        {!loadError && promos.length === 0 && (
          <p style={{ fontSize: 12, color: '#888' }}>No promo codes yet. Add one below.</p>
        )}
        {promos.map(p => {
          const expired = p.expires_on && p.expires_on < today;
          return (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(74,46,26,0.06)' }}>
              <div>
                <span style={{ fontFamily: 'monospace', fontWeight: 500, fontSize: 13 }}>{p.code}</span>
                <span style={{ fontSize: 12, color: '#888', marginLeft: 10 }}>{p.discount_percent + '% off'}</span>
                {p.expires_on && (
                  <span style={{ fontSize: 11, marginLeft: 10, color: expired ? '#8B2020' : '#888' }}>
                    {expired ? 'Expired ' + niceDate(p.expires_on) : 'Expires ' + niceDate(p.expires_on)}
                  </span>
                )}
              </div>
              <button
                className={'adm-btn ' + (p.is_active ? 'adm-btn--outline' : 'adm-btn--danger')}
                disabled={busy}
                onClick={() => toggle(p)}
                style={{ fontSize: 11, padding: '4px 10px' }}
              >
                {p.is_active ? 'Active' : 'Turned off'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="adm-card">
        <div className="adm-card-title"><IconPlus size={14} /> Add new code</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="adm-input"
            value={newCode}
            onChange={e => setNewCode(e.target.value.toUpperCase().replace(/\s/g, ''))}
            placeholder="Code (e.g. SUMMER20)"
            maxLength={30}
            style={{ flex: 1, minWidth: 160, fontFamily: 'monospace' }}
          />
          <input
            className="adm-input"
            type="number"
            min="1"
            max="90"
            value={newDiscount}
            onChange={e => setNewDiscount(e.target.value)}
            style={{ width: 70 }}
          />
          <span style={{ fontSize: 12, color: '#888' }}>% off</span>
          <input
            className="adm-input"
            type="date"
            value={newExpiry}
            onChange={e => setNewExpiry(e.target.value)}
            title="Optional: last day the code works"
            style={{ width: 150 }}
          />
          <button className="adm-btn" disabled={busy || !!loadError} onClick={addPromo} style={{ opacity: busy ? 0.6 : 1 }}>
            <IconDeviceFloppy size={14} /> {busy ? 'Saving...' : 'Add'}
          </button>
        </div>
        <p style={{ fontSize: 10, color: '#888', marginTop: 8, lineHeight: 1.5 }}>
          The date is optional: it's the last day the code works (Guyana time). Leave it blank for no expiry. Discounts can be 1% to 90%.
        </p>
      </div>
    </div>
  );
}